import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ButtonStyles,
  ButtonSize,
  Button,
  ButtonWidth,
  useBreakpoint,
  Card,
  HeaderStyle,
} from '../../../ui/components';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { LoggerService, PAGES, trackPage } from '../../../ui/utils';
import { DynamicObjectInterface } from '../../../sdk/type';
import ParticipantService from '../../../sdk/services/participantService';
import { useAPICall } from '../../../hooks';
import TableWithSearchFilterTemplate from '../../../ui/components/TableWithSearchFilterTemplate/TableWithSearchFilterTemplate';
import { DashboardProps } from '../dashboardConfig';
import styles from './index.module.scss';
import { getTeamListConfig } from './teamListConfig';

export interface TeamData {
  name?: string;
  totalPoints?: number;
  weightIn?: number;
  weightOut?: number;
  purchase?: string[];
  subscribe?: string[];
  teamLeader?: boolean;
  subscriptionMessage?: boolean;
  status?: string;
  newAbo?: boolean;
  result?: string | null;
}
[];

export interface TeamDetails {
  name: string;
  coachId: string;
  coachName: string;
  teamStartDate: string;
  teamEndDate: string;
  teamId: number;
  teamPoint: number;
  teamStatus: string;
  isAllPurchase: boolean;
  isAllWeightIn: boolean;
  isAllWeightOut: boolean;
  isAllWin: boolean;
  isAllSubscribe: boolean;
  participants: [];
  totalRecords: number;
}

export const TeamList = (props: DashboardProps) => {
  const [data, setData] = useState<TeamDetails[]>([]);
  const [filteredData, setFilteredData] = useState<TeamDetails[]>([]);
  const [searchKey, setSearchKey] = useState('');

  const apiCall = useAPICall();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;

  const initialFilterList = [
    {
      filterName: t('dashboard.coach-details.team-list.label.preAssessment'),
      filterOptions: [
        { label: 'Completed', checked: false },
        { label: 'Not Completed', checked: false },
      ],
      isOpen: false,
    },
    {
      filterName: t('dashboard.coach-details.team-list.label.postAssessment'),
      filterOptions: [
        { label: 'Completed', checked: false },
        { label: 'Not Completed', checked: false },
      ],
      isOpen: false,
    },
    {
      filterName: t('dashboard.coach-details.team-list.label.achievementOfResults'),
      filterOptions: [
        { label: 'Achieved', checked: false },
        { label: 'Not Achieved', checked: false },
      ],
      isOpen: false,
    },
    // {
    //   filterName: 'Subscribed to ALP',
    //   filterOptions: [
    //     { label: 'Subscribed', checked: false },
    //     { label: 'Not Subscribed', checked: false },
    //   ],
    //   isOpen: false,
    // },
  ];

  const [filterList, setFilterList] = useState(initialFilterList);
  const [tempFilterList, setTempFilterList] = useState(initialFilterList);

  useEffect(() => {
    const timeout = setTimeout(() => {
      trackPage(PAGES.teamList);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (props.campaignData && props.decodedData && props.index !== undefined) {
        const result = await apiCall(ParticipantService.getTeamListByUser, {
          campaignId: props?.campaignData?.[props?.index]?.campaignId,
          loginId: props.decodedData?.loginId,
          userId: props.decodedData?.userId,
          coachId:
            props.callFrom !== 'coachList'
              ? props?.campaignData?.[props?.index]?.enrollment?.find(
                  (i: DynamicObjectInterface) => i.enrollmentType === 'COACH',
                )?.coachId
              : props.coachId,
          enrollmentStartDate: props.enrollmentStartDate,
          enrollmentEndDate: props.enrollmentEndDate,
          maxLength: props.callFrom === 'coachList' ? 7 : null,
        });

        const data = result?.data?.data?.map((team: DynamicObjectInterface) => {
          const totalPoints = team?.participants
            ?.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
            ?.map((participant: DynamicObjectInterface) => participant.point)
            ?.flatMap((item: DynamicObjectInterface) => Object.values(item))
            ?.reduce((acc: number, value: number) => acc + value, 0)
            ?.toFixed(2);

          const isAllPurchase =
            team.participants.filter(
              (participant: DynamicObjectInterface) =>
                participant.purchase.length > 0 && participant.status === 'ACTIVE',
            )?.length >= parseInt(team.teamSize, 10);

          const isAllWeightIn =
            team.participants.filter(
              (participant: DynamicObjectInterface) =>
                !!participant.weightIn?.weightInDate && participant.status === 'ACTIVE',
            )?.length >= parseInt(team.teamSize, 10);

          const isAllWeightOut =
            team.participants.filter(
              (participant: DynamicObjectInterface) =>
                !!participant.weightOut?.weightOutDate && participant.status === 'ACTIVE',
            )?.length >= parseInt(team.teamSize, 10);

          const isAllWin =
            team.participants.filter(
              (participant: DynamicObjectInterface) =>
                !!participant.isIndividualAchieved && participant.status === 'ACTIVE',
            )?.length >= parseInt(team.teamSize, 10);

          const isAllValidPurchase =
            team.participants.filter(
              (participant: DynamicObjectInterface) =>
                participant.purchase[0]?.orderStatus === 1 && participant.status === 'ACTIVE',
            )?.length >= parseInt(team.teamSize, 10);

          // const isAllSubscribe =
          //   team.participants.filter(
          //     (participant: DynamicObjectInterface) =>
          //       participant.subscribe.length > 0 && participant.status === 'ACTIVE',
          //   )?.length >= parseInt(team.teamSize, 10);
          return {
            ...team,
            name: team.teamName,
            coachId: team.coachId,
            coachName: team.coachName,
            teamStartDate: team.teamStartDate,
            teamEndDate: team.teamEndDate,
            teamSize: team.teamSize,
            teamId: team.teamId,
            teamStatus: team.status,
            teamPoint: totalPoints,
            isAllPurchase: isAllPurchase,
            isAllWeightIn: isAllWeightIn,
            isAllWeightOut: isAllWeightOut,
            isAllWin: isAllWin,
            isAllValidPurchase: isAllValidPurchase,
            isCoachEnrollment: props?.campaignData?.[props?.index]?.enrollment.some(
              (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
            ),
            participants: team.participants,
          };
        });

        setData(data);
        setFilteredData(data);
      }
    })();
  }, [props.campaignData, props.decodedData, props.index]);

  const handleOnRowClick = (rowData: DynamicObjectInterface) => {
    navigate(`/campaigns/${props?.campaignData?.[props?.index]?.campaignId}/teams/${rowData?.teamId}`);
  };

  const handleSearch = (text: string) => {
    setSearchKey(text);

    // Then filter the already filtered data based on the search key
    const newFilteredData = data.filter(team => {
      return tempFilterList.every(filter => {
        const selectedOption = filter.filterOptions.find(option => option.checked);
        if (!selectedOption) {
          return true; // No filter option selected, include all
        }
        const filterName = filter.filterName;
        LoggerService.log('team', team);
        if (filterName === t('dashboard.coach-details.team-list.label.preAssessment')) {
          return selectedOption.label === 'Completed' ? team?.isAllWeightIn : !team?.isAllWeightIn;
        } else if (filterName === t('dashboard.coach-details.team-list.label.postAssessment')) {
          return selectedOption.label === 'Completed' ? team?.isAllWeightOut : !team?.isAllWeightOut;
        } else if (filterName === t('dashboard.coach-details.team-list.label.achievementOfResults')) {
          return selectedOption.label === 'Achieved' ? team?.isAllWin : !team?.isAllWin;
        } else if (filterName === 'Subscribed to ALP') {
          return selectedOption.label === 'Subscribed' ? team?.isAllSubscribe : !team?.isAllSubscribe;
        }
        return true; // Include all if filter name doesn't match
      });
    });

    const finalFilteredData = newFilteredData.filter(i => i?.name?.toLowerCase()?.includes(text?.toLowerCase()));

    setFilteredData(finalFilteredData);
  };

  const handleToggleFilterOpen = (filterIndex: number) => {
    setTempFilterList(prevList =>
      prevList.map((filter, index) => (index === filterIndex ? { ...filter, isOpen: !filter.isOpen } : filter)),
    );
  };

  const handleToggleOptionChecked = (filterIndex: number | undefined, optionIndex: number) => {
    setTempFilterList(prevList =>
      prevList.map((filter, index) => ({
        ...filter,
        filterOptions: filter.filterOptions.map((option, idx) => ({
          ...option,
          checked: index === filterIndex && idx === optionIndex ? !option.checked : false,
        })),
      })),
    );
  };

  const handleCancel = () => {
    // Reset the tempFilterList to match the filterList (the original state)
    setTempFilterList(
      filterList.map(filter => ({
        ...filter,
        isOpen: false, // Close all filters
      })),
    );
  };

  const handleApplyChanges = async () => {
    await handleSearch(searchKey);

    // Update the main filter list state
    setFilterList(tempFilterList);

    // Close all filter drawers
    setTempFilterList(prevList =>
      prevList.map(filter => ({
        ...filter,
        isOpen: false,
      })),
    );
  };

  const handleResetFilters = () => {
    setFilterList(initialFilterList);
    setTempFilterList(initialFilterList);
    setFilteredData(data);
    setSearchKey('');
  };

  return (
    // hide team list section when teamList is empty, only for props.callFrom === 'dashboard'
    <>
      {data?.length > 0 ? (
        <div className={styles['dashboard-page']}>
          {props.callFrom !== 'coachList' && (
            <div className={styles['dashboard-page-title']}>{t('dashboard.coach-details.team-list.label.teams')}</div>
          )}

          {isDesktopView ? (
            <Card
              title={`${t('dashboard.coach-details.team-list.label.campaign')}: ${props.campaignData[props.index]?.campaignName}`}
              headerStyle={HeaderStyle.HEADER_5}
            >
              <TableWithSearchFilterTemplate
                dataSource={filteredData}
                getColumns={getTeamListConfig}
                rowKey="recId"
                handleSearch={handleSearch}
                handleReset={() => handleSearch('')}
                onRowClick={handleOnRowClick}
                searchKey={searchKey}
                tempFilterList={tempFilterList}
                filterList={filterList}
                onToggleFilterOpen={handleToggleFilterOpen}
                onToggleOptionChecked={handleToggleOptionChecked}
                handleApplyChanges={handleApplyChanges}
                onClose={handleCancel} // Pass handleCancel as onClose
                handleResetFilters={handleResetFilters}
                hideSearchFilter={props.callFrom === 'coachList'}
              />
            </Card>
          ) : (
            <>
              <div className={styles['dashboard-page-subtitle']}>
                {`${t('dashboard.coach-details.team-list.label.campaign')} : ${props.campaignData[props.index]?.campaignName}`}
              </div>
              <TableWithSearchFilterTemplate
                dataSource={filteredData}
                getColumns={getTeamListConfig}
                rowKey="recId"
                handleSearch={handleSearch}
                handleReset={() => handleSearch('')}
                onRowClick={handleOnRowClick}
                searchKey={searchKey}
                tempFilterList={tempFilterList}
                filterList={filterList}
                onToggleFilterOpen={handleToggleFilterOpen}
                onToggleOptionChecked={handleToggleOptionChecked}
                handleApplyChanges={handleApplyChanges}
                onClose={handleCancel} // Pass handleCancel as onClose
                handleResetFilters={handleResetFilters}
                hideSearchFilter={props.callFrom === 'coachList'}
              />
            </>
          )}
          {props.callFrom === 'coachList' && (
            <>
              {filteredData?.[0]?.totalRecords > 6 && (
                <Button
                  size={ButtonSize.MEDIUM}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => navigate(`/campaigns/${props?.campaignData?.[0]?.campaignId}/teams`)}
                  className={styles['view-all-button']}
                  title={`${t('dashboard.coach-details.team-list.label.viewAll')} >`}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} className={styles['team-details-opt-card']} />
      )}
    </>
  );
};
